/**
 * ChatInput Component
 * Input field with send button for chat messages
 */

import React, { useMemo } from "react";
import { TextField, IconButton, CircularProgress, useTheme, alpha, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import SendIcon from "@mui/icons-material/Send";

const ChatInput = ({ message, onChange, onSend, disabled, isLoading, messagesRemaining, messagesLimit, hasMessageLimit }) => {
        const { t } = useTranslation();
        const theme = useTheme();

        const handleKeyPress = (e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                        onSend();
                }
        };

        // Determine the color based on remaining messages
        const limitIndicatorColor = useMemo(() => {
                if (!hasMessageLimit || messagesRemaining === null) return null;
                if (messagesRemaining <= 1) return theme.palette.error.main;
                if (messagesRemaining <= 5) return theme.palette.warning.main;
                return theme.palette.text.secondary;
        }, [hasMessageLimit, messagesRemaining, theme]);

        return (
                <Box sx={{ width: "100%" }}>
                        <TextField
                                fullWidth
                                variant="outlined"
                                placeholder={t("ai-chat-placeholder") || "Ask about animals, treatments, predictions..."}
                                value={message}
                                onChange={onChange}
                                onKeyPress={handleKeyPress}
                                size="small"
                                disabled={disabled}
                                sx={{
                                        "& .MuiInputBase-root": {
                                                borderRadius: "12px",
                                                background: alpha(theme.palette.background.paper, 0.5),
                                                backdropFilter: "blur(10px)",
                                                transition: "all 0.2s ease",
                                                "&:hover": {
                                                        background: alpha(theme.palette.background.paper, 0.7),
                                                        transform: "translateY(-1px)",
                                                },
                                                "&.Mui-focused": {
                                                        background: alpha(theme.palette.background.paper, 0.8),
                                                        transform: "translateY(-1px)",
                                                },
                                        },
                                        "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                        borderColor: alpha(theme.palette.divider, 0.3),
                                                        borderWidth: "1px",
                                                },
                                                "&:hover fieldset": {
                                                        borderColor: alpha(theme.palette.primary.main, 0.5),
                                                        borderWidth: "1px",
                                                },
                                                "&.Mui-focused fieldset": {
                                                        borderColor: theme.palette.primary.main,
                                                        borderWidth: "2px",
                                                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                                                },
                                        },
                                }}
                                inputProps={{ maxLength: 4000 }}
                                InputProps={{
                                        endAdornment: (
                                                <IconButton
                                                        color="primary"
                                                        onClick={onSend}
                                                        disabled={disabled || !message.trim()}
                                                        size="small"
                                                >
                                                        {isLoading ? (
                                                                <CircularProgress size={20} />
                                                        ) : (
                                                                <SendIcon />
                                                        )}
                                                </IconButton>
                                        ),
                                }}
                        />
                        {hasMessageLimit && messagesRemaining !== null && messagesLimit !== null && (
                                <Typography
                                        variant="caption"
                                        sx={{
                                                display: "block",
                                                mt: 0.5,
                                                ml: 1,
                                                color: limitIndicatorColor,
                                                fontSize: "0.75rem",
                                        }}
                                >
                                        {t("chat-messages-remaining", { remaining: messagesRemaining, limit: messagesLimit })}
                                </Typography>
                        )}
                </Box>
        );
};

export default ChatInput;
